/* ─────────────────────────────────────────────────────────────────────────────
   Vistas previas de los tickets que no son el ticket de venta.

   El ticket de venta se dibuja con `generarTicketHTML` (el mismo generador que
   usa la impresión real). Los demás formatos no tienen generador en el
   frontend, así que aquí se arma una maqueta que refleja los interruptores de
   cada sección para que se vea el efecto antes de guardar.
   ───────────────────────────────────────────────────────────────────────── */

/** Comanda de ejemplo usada por la vista previa del ticket de venta. */
export const TICKET_DEMO = {
  mesa_numero: 3,
  mesero: 'Ana Mesera',
  items: [
    { producto: 'Filete a la plancha', cantidad: 1, precio_unitario: 280, importe: 280, notas: null },
    { producto: 'Agua mineral',        cantidad: 2, precio_unitario: 45,  importe: 90,  notas: null },
  ],
  subtotal: 318.97, iva: 51.03, total: 370,
  descuento_tipo: null, descuento_valor: 0, descuento_monto: 0,
  metodo: 'efectivo', monto_recibido: 400, propina: 30, propina_pct: 8.11,
  cambio: 0, fecha: new Date().toISOString(),
};

const papel = (contenido: string, fuente = 13) =>
  `<div style="font-family:'Courier New',monospace;font-size:${fuente}px;padding:14px;max-width:300px;margin:0 auto;background:#fff;color:#111;">${contenido}</div>`;

const hr = `<hr style="border:none;border-top:1px dashed #999;margin:6px 0;"/>`;
const fila = (l: string, v: string, bold = false) =>
  `<div style="display:flex;justify-content:space-between;${bold ? 'font-weight:bold;' : ''}"><span>${l}</span><span>${v}</span></div>`;
const bloque = (t: string) => `${hr}<div style="font-weight:bold;margin:2px 0;">${t}</div>`;
const activo = (v?: string) => v !== 'false';

export function mockupComanda(area: string, titulo: string, mostrarMesero?: string, mostrarHora?: string) {
  const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  return papel(`
    <div style="text-align:center;font-weight:bold;font-size:16px;">*** ${titulo || area} ***</div>
    ${activo(mostrarMesero) ? '<div>Mesa: 3   Mesero: Ana Mesera</div>' : '<div>Mesa: 3</div>'}
    ${activo(mostrarHora) ? `<div>${hora}</div>` : ''}
    ${hr}
    <div>1x  Filete a la plancha</div>
    <div>2x  Agua mineral</div>
    <div style="font-size:11px;color:#666;">   → sin sal</div>
    ${hr}
  `);
}

export function mockupPrecuenta(titulo: string, mensaje: string, mostrarPrecios: string, restaurante: string) {
  return papel(`
    ${restaurante ? `<div style="text-align:center;font-weight:bold;">${restaurante}</div>` : ''}
    <div style="text-align:center;font-weight:bold;">${titulo || 'PRE-CUENTA'}</div>
    <div style="text-align:center;font-size:11px;">Mesa: 3   Mesero: Ana</div>
    ${hr}
    ${activo(mostrarPrecios) ? `
      ${fila('1x Filete', '$280.00')}
      ${fila('2x Agua', '$90.00')}
      ${hr}
      ${fila('TOTAL', '$370.00', true)}
    ` : `
      <div>Filete a la plancha</div>
      <div>2x Agua mineral</div>
    `}
    ${hr}
    <div style="text-align:center;font-size:11px;">${mensaje || 'ESTE NO ES UN COMPROBANTE FISCAL'}</div>
  `, 12);
}

export function mockupCorte(cfg: Record<string, string>) {
  const ahora = new Date().toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const ver = (k: string) => activo(cfg[k]);

  return papel(`
    <div style="text-align:center;font-weight:bold;">${cfg.corte_titulo || 'CORTE DE TURNO'}</div>
    <div style="text-align:center;font-size:11px;">${ahora}</div>
    <div style="text-align:center;font-size:11px;">Cajero: Luis Hernández</div>
    ${bloque('RESUMEN DE CAJA')}
    ${fila('Efectivo inicial', '$500.00')}${fila('Efectivo (ventas)', '$1,250.00')}
    ${fila('Tarjeta', '$1,800.00')}${fila('Transferencias', '$370.00')}
    ${fila('Retiros en efectivo', '-$200.00')}${fila('Depositos en efectivo', '$0.00')}
    ${hr}${fila('Saldo final', '$1,550.00', true)}${fila('Efectivo final', '$1,550.00', true)}
    ${ver('corte_mostrar_formas_pago') ? `${bloque('FORMAS DE PAGO')}
      ${fila('Efectivo', '$1,250.00')}${fila('Tarjeta', '$1,800.00')}${fila('Transferencias', '$370.00')}
      ${hr}${fila('TOTAL VENTAS', '$3,420.00', true)}` : ''}
    ${ver('corte_mostrar_comisiones') && cfg.comisiones_activas === 'true' ? `${bloque('COMISIONES DE TERMINAL')}
      ${(() => {
        // Ejemplo con los $1,800 de tarjeta del resumen de arriba, a la tasa capturada
        const efectiva = (parseFloat(cfg.comisiones_pct) || 0) * (cfg.comisiones_iva !== 'false' ? 1.16 : 1);
        const comision = 1800 * (efectiva / 100) + (parseFloat(cfg.comisiones_fijo) || 0) * 1.16 * 12;
        const money = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        return `${fila('Tasa aplicada', `${efectiva.toFixed(2)}%`)}
          ${fila('Tarjeta (12)', money(1800))}${fila('  Comision', `-${money(comision)}`)}${fila('  Neto', money(1800 - comision))}
          ${hr}${fila('Total comisiones', `-${money(comision)}`, true)}${fila('Neto a depositar', money(1800 - comision), true)}`;
      })()}` : ''}
    ${ver('corte_mostrar_propinas') ? `${bloque('PROPINAS POR FORMA DE PAGO')}
      ${fila('Efectivo', '$100.00')}${fila('Tarjeta', '$60.00')}${fila('Transferencia', '$20.00')}
      ${hr}${fila('Total propinas', '$180.00', true)}` : ''}
    ${ver('corte_mostrar_categorias') ? `${bloque('VENTA POR TIPO DE PRODUCTO')}
      ${fila('Bebidas', '$890.00')}${fila('De la casa', '$780.00')}${fila('Ensaladas', '$560.00')}` : ''}
    ${ver('corte_mostrar_cuentas') ? `${bloque('RESUMEN DE CUENTAS')}
      ${fila('Cuentas normales', '14')}${fila('Cuentas canceladas', '1')}
      ${fila('Cuentas con cortesias', '0')}${fila('Cuentas con descuentos', '2')}
      ${hr}${fila('Cuenta promedio', '$244.00')}${fila('Consumo promedio', '$122.00')}
      ${fila('Total de comensales', '28')}
      ${hr}${fila('Propina cargos', '$180.00')}${fila('Cortesias alimentos', '$0.00')}${fila('Cortesias bebidas', '$0.00')}` : ''}
    ${ver('corte_mostrar_declaracion') ? `${bloque('DECLARACION')}
      <div style="font-weight:bold;">EFECTIVO</div>
      ${fila('  Sistema', '$1,550.00')}${fila('  Declarado', '$1,550.00')}${fila('  Diferencia', '+$0.00')}` : ''}
    ${ver('corte_mostrar_firma') ? `${hr}
      <div>Cajero: Luis Hernández</div>
      <div style="margin-top:6px;">Nombre: _______________________</div>
      <div style="margin-top:6px;">Firma cajero: __________________</div>
      <div style="margin-top:6px;">Firma gerente: _________________</div>` : ''}
  `, 12);
}

export function mockupPropinas(titulo: string, mostrarFirma: string) {
  const ahora = new Date().toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return papel(`
    <div style="text-align:center;font-weight:bold;">${titulo || 'PROPINAS DEL TURNO'}</div>
    <div style="text-align:center;font-size:11px;">${ahora}</div>
    <div style="text-align:center;font-size:11px;">Luis Hernández</div>
    ${hr}
    ${fila('Ana Mesera', '$120.00')}
    ${fila('Carlos Mesero', '$60.00')}
    ${hr}
    ${fila('Total propinas', '$180.00', true)}
    ${activo(mostrarFirma) ? '<div style="margin-top:8px;">Firma mesero ___________________</div>' : ''}
  `, 12);
}
